// Port of backend-ig/pkg/scope/scope.go (Parse, Match, CheckAccess and helpers).
// Keep the behaviour identical to the Go code, so frontend and backend agree.
//
// Scope format: method:ownership:path. A lone "*" is the superadmin wildcard.

export type Ownership = 'own' | 'admin' | '';

export type Scope = {
  method: string;
  ownership: string;
  path: string;
};

const METHOD_ALL = '*';
const OWNERSHIP_OWN = 'own';
const OWNERSHIP_ADMIN = 'admin';
const OWNERSHIP_ALL = '*';
const WILDCARD = '*';
const SCOPE_SEGMENTS = 3;

function validate(s: Scope): boolean {
  if (s.method !== 'read' && s.method !== 'write' && s.method !== METHOD_ALL) {
    return false;
  }

  if (
    s.ownership !== OWNERSHIP_OWN &&
    s.ownership !== OWNERSHIP_ADMIN &&
    s.ownership !== OWNERSHIP_ALL
  ) {
    return false;
  }

  return s.path !== '';
}

// parse returns null for a malformed scope (Go returns an error).
export function parse(raw: string): Scope | null {
  if (raw === WILDCARD) {
    return { method: METHOD_ALL, ownership: OWNERSHIP_ALL, path: WILDCARD };
  }

  // Go strings.SplitN(s, ":", 3): the third part keeps any further colons.
  const first = raw.indexOf(':');
  if (first < 0) {
    return null;
  }

  const second = raw.indexOf(':', first + 1);
  if (second < 0) {
    return null;
  }

  const parts = [raw.slice(0, first), raw.slice(first + 1, second), raw.slice(second + 1)];
  if (parts.length !== SCOPE_SEGMENTS) {
    return null;
  }

  const sc: Scope = { method: parts[0], ownership: parts[1], path: parts[2] };

  return validate(sc) ? sc : null;
}

function methodMatches(scopeMethod: string, required: string): boolean {
  return scopeMethod === METHOD_ALL || scopeMethod === required;
}

// Matches segment by segment, as pathMatches in pkg/scope/scope.go.
// A "*" that ends the scope path is a trailing wildcard: the last scope segment
// is a prefix of the request segment and further segments are accepted.
// Any other segment that is exactly "*" matches one non-empty request segment.
// A "*" inside a longer segment is a literal character.
function pathMatches(scopePath: string, requestPath: string): boolean {
  if (scopePath === WILDCARD || scopePath === requestPath) {
    return true;
  }

  const trailing = scopePath.endsWith('*');
  const scopeSegs = (trailing ? scopePath.slice(0, -1) : scopePath).split('/');
  const reqSegs = requestPath.split('/');

  if (trailing ? reqSegs.length < scopeSegs.length : reqSegs.length !== scopeSegs.length) {
    return false;
  }

  const last = scopeSegs.length - 1;

  for (let i = 0; i < scopeSegs.length; i++) {
    const seg = scopeSegs[i];
    if (trailing && i === last) {
      if (!reqSegs[i].startsWith(seg)) {
        return false;
      }
    } else if (seg === WILDCARD) {
      if (reqSegs[i] === '') {
        return false;
      }
    } else if (seg !== reqSegs[i]) {
      return false;
    }
  }

  return true;
}

function ownershipRank(o: string): number {
  switch (o) {
    case OWNERSHIP_OWN:
      return 1;
    case OWNERSHIP_ADMIN:
    case OWNERSHIP_ALL:
      return 2;
    default:
      return 0;
  }
}

export function match(s: Scope, method: string, path: string): boolean {
  if (!methodMatches(s.method, method)) {
    return false;
  }

  return pathMatches(s.path, path);
}

export function checkAccess(
  scopes: string[],
  method: string,
  path: string,
): { allowed: boolean; ownership: Ownership } {
  let allowed = false;
  let best = '';

  for (const raw of scopes) {
    const s = parse(raw);
    if (s === null) {
      continue;
    }

    if (!match(s, method, path)) {
      continue;
    }

    allowed = true;

    if (ownershipRank(s.ownership) > ownershipRank(best)) {
      best = s.ownership;
    }
  }

  // Normalise "*" to "admin" for consumers.
  if (best === OWNERSHIP_ALL) {
    best = OWNERSHIP_ADMIN;
  }

  return { allowed, ownership: best as Ownership };
}
